/**
 * Product Roadmap Store
 *
 * Manages Product Roadmap documents and their items (cards).
 * Items are placed in one of three time horizons: now, next, later.
 * Items can optionally link to workspace items (item_id) so they
 * are shared across planning artifacts like Back Plans.
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────

export type ProductRoadmapHorizon = 'now' | 'next' | 'later';
export type ProductRoadmapConfidence = 'high' | 'medium' | 'uncertain';
export type ProductRoadmapItemStatus = 'planned' | 'in_progress' | 'done' | 'dropped';
export type ProductRoadmapDocumentStatus = 'draft' | 'active' | 'on_hold' | 'completed' | 'archived';

export interface ProductRoadmapDocument {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  status: ProductRoadmapDocumentStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductRoadmapItem {
  id: string;
  roadmapId: string;
  workspaceId: string;
  title: string;
  description: string | null;
  horizon: ProductRoadmapHorizon;
  confidence: ProductRoadmapConfidence;
  status: ProductRoadmapItemStatus;
  position: number;
  tags: string[];
  /** Linked workspace item; null = roadmap-only until promoted to workspace item. */
  itemId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Store interface ───────────────────────────────────────

interface ProductRoadmapState {
  documents: ProductRoadmapDocument[];
  items: ProductRoadmapItem[];
  isLoading: boolean;
  loadedRoadmapId: string | null;
  error: string | null;

  // Document CRUD
  fetchDocuments: () => Promise<void>;
  fetchProductRoadmap: (roadmapId: string) => Promise<void>;
  createProductRoadmap: (input: { title: string; description?: string }) => Promise<{ roadmap: ProductRoadmapDocument | null; error?: string }>;
  updateProductRoadmap: (id: string, updates: Partial<Pick<ProductRoadmapDocument, 'title' | 'description' | 'status'>>) => Promise<{ success: boolean; error?: string }>;
  deleteProductRoadmap: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Item CRUD
  createItem: (roadmapId: string, input: Partial<ProductRoadmapItem>) => Promise<{ item: ProductRoadmapItem | null; error?: string }>;
  updateItem: (id: string, updates: Partial<ProductRoadmapItem>) => Promise<{ success: boolean; error?: string }>;
  deleteItem: (id: string) => Promise<{ success: boolean; error?: string }>;
  moveItem: (id: string, horizon: ProductRoadmapHorizon) => Promise<{ success: boolean; error?: string }>;

  // Selectors
  getDocumentById: (id: string) => ProductRoadmapDocument | undefined;
  getItemsForRoadmap: (roadmapId: string) => ProductRoadmapItem[];
  getItemsForHorizon: (roadmapId: string, horizon: ProductRoadmapHorizon) => ProductRoadmapItem[];
}

// ── Helpers ───────────────────────────────────────────────

function rowToDoc(r: Record<string, unknown>): ProductRoadmapDocument {
  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    title: (r.title as string) ?? 'Untitled Product Roadmap',
    description: (r.description as string) ?? null,
    status: ((r.status as string) ?? 'draft') as ProductRoadmapDocumentStatus,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function rowToItem(r: Record<string, unknown>): ProductRoadmapItem {
  return {
    id: r.id as string,
    roadmapId: r.roadmap_id as string,
    workspaceId: r.workspace_id as string,
    title: (r.title as string) ?? '',
    description: (r.description as string) ?? null,
    horizon: ((r.horizon as string) ?? 'now') as ProductRoadmapHorizon,
    confidence: ((r.confidence as string) ?? 'high') as ProductRoadmapConfidence,
    status: ((r.status as string) ?? 'planned') as ProductRoadmapItemStatus,
    position: (r.position as number) ?? 0,
    tags: (r.tags as string[]) ?? [],
    itemId: (r.item_id as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

// ── Store ─────────────────────────────────────────────────

export const useProductRoadmapStore = create<ProductRoadmapState>()((set, get) => ({
  documents: [],
  items: [],
  isLoading: false,
  loadedRoadmapId: null,
  error: null,

  // ── Document CRUD ───────────────────────────────────────

  fetchDocuments: async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('product_roadmap_documents')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ documents: (data ?? []).map(rowToDoc), isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchProductRoadmap: async (roadmapId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    set({ isLoading: true, error: null });
    try {
      const [docResult, itemsResult] = await Promise.all([
        supabase
          .from('product_roadmap_documents')
          .select('*')
          .eq('id', roadmapId)
          .eq('workspace_id', workspaceId)
          .single(),
        supabase
          .from('product_roadmap_items')
          .select('*')
          .eq('roadmap_id', roadmapId)
          .eq('workspace_id', workspaceId)
          .order('position', { ascending: true }),
      ]);

      if (docResult.error) throw docResult.error;

      const newDoc = rowToDoc(docResult.data as Record<string, unknown>);
      const newItems = (itemsResult.data ?? []).map(rowToItem);

      set((state) => ({
        documents: state.documents.some((d) => d.id === roadmapId)
          ? state.documents.map((d) => (d.id === roadmapId ? newDoc : d))
          : [...state.documents, newDoc],
        items: [
          ...state.items.filter((i) => i.roadmapId !== roadmapId),
          ...newItems,
        ],
        loadedRoadmapId: roadmapId,
        isLoading: false,
      }));
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  createProductRoadmap: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { roadmap: null, error: 'Supabase unavailable' };
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return { roadmap: null, error: 'No workspace' };

    const id = generateUUID();
    const now = new Date().toISOString();
    const optimistic: ProductRoadmapDocument = {
      id,
      workspaceId,
      title: input.title || 'Untitled Product Roadmap',
      description: input.description ?? null,
      status: 'draft',
      createdBy: null,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({ documents: [optimistic, ...state.documents] }));

    try {
      const { data, error } = await supabase
        .from('product_roadmap_documents')
        .insert({ id, workspace_id: workspaceId, title: optimistic.title, description: optimistic.description })
        .select()
        .single();

      if (error) throw error;
      const saved = rowToDoc(data as Record<string, unknown>);
      set((state) => ({
        documents: state.documents.map((d) => (d.id === id ? saved : d)),
      }));
      return { roadmap: saved };
    } catch (e) {
      set((state) => ({ documents: state.documents.filter((d) => d.id !== id) }));
      return { roadmap: null, error: (e as Error).message };
    }
  },

  updateProductRoadmap: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const snapshot = get().documents.find((d) => d.id === id);
    if (!snapshot) return { success: false, error: 'Not found' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.status !== undefined) dbUpdates.status = updates.status;

    set((state) => ({
      documents: state.documents.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    }));

    try {
      const { error } = await supabase
        .from('product_roadmap_documents')
        .update(dbUpdates)
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (e) {
      set((state) => ({
        documents: state.documents.map((d) => (d.id === id ? snapshot : d)),
      }));
      return { success: false, error: (e as Error).message };
    }
  },

  deleteProductRoadmap: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const snapshot = get().documents.find((d) => d.id === id);
    const snapshotItems = get().items.filter((i) => i.roadmapId === id);

    set((state) => ({
      documents: state.documents.filter((d) => d.id !== id),
      items: state.items.filter((i) => i.roadmapId !== id),
    }));

    try {
      const { error } = await supabase
        .from('product_roadmap_documents')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (e) {
      if (snapshot) {
        set((state) => ({
          documents: [...state.documents, snapshot],
          items: [...state.items, ...snapshotItems],
        }));
      }
      return { success: false, error: (e as Error).message };
    }
  },

  // ── Item CRUD ───────────────────────────────────────────

  createItem: async (roadmapId, input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { item: null, error: 'Supabase unavailable' };
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return { item: null, error: 'No workspace' };

    const existingInHorizon = get().items.filter(
      (i) => i.roadmapId === roadmapId && i.horizon === (input.horizon ?? 'now')
    );
    const maxPos = existingInHorizon.reduce((m, i) => Math.max(m, i.position), -1);

    const id = generateUUID();
    const now = new Date().toISOString();
    const optimistic: ProductRoadmapItem = {
      id,
      roadmapId,
      workspaceId,
      title: input.title ?? '',
      description: input.description ?? null,
      horizon: input.horizon ?? 'now',
      confidence: input.confidence ?? 'high',
      status: input.status ?? 'planned',
      position: maxPos + 1,
      tags: input.tags ?? [],
      itemId: input.itemId ?? null,
      createdBy: null,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({ items: [...state.items, optimistic] }));

    try {
      const { data, error } = await supabase
        .from('product_roadmap_items')
        .insert({
          id,
          roadmap_id: roadmapId,
          workspace_id: workspaceId,
          title: optimistic.title,
          description: optimistic.description,
          horizon: optimistic.horizon,
          confidence: optimistic.confidence,
          status: optimistic.status,
          position: optimistic.position,
          tags: optimistic.tags,
          item_id: optimistic.itemId,
        })
        .select()
        .single();

      if (error) throw error;
      const saved = rowToItem(data as Record<string, unknown>);
      set((state) => ({
        items: state.items.map((i) => (i.id === id ? saved : i)),
      }));
      return { item: saved };
    } catch (e) {
      set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
      return { item: null, error: (e as Error).message };
    }
  },

  updateItem: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const snapshot = get().items.find((i) => i.id === id);
    if (!snapshot) return { success: false, error: 'Not found' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.horizon !== undefined) dbUpdates.horizon = updates.horizon;
    if (updates.confidence !== undefined) dbUpdates.confidence = updates.confidence;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.itemId !== undefined) dbUpdates.item_id = updates.itemId;

    set((state) => ({
      items: state.items.map((i) => (i.id === id ? { ...i, ...updates } : i)),
    }));

    try {
      const { error } = await supabase
        .from('product_roadmap_items')
        .update(dbUpdates)
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (e) {
      set((state) => ({
        items: state.items.map((i) => (i.id === id ? snapshot : i)),
      }));
      return { success: false, error: (e as Error).message };
    }
  },

  deleteItem: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const snapshot = get().items.find((i) => i.id === id);
    set((state) => ({ items: state.items.filter((i) => i.id !== id) }));

    try {
      const { error } = await supabase
        .from('product_roadmap_items')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (e) {
      if (snapshot) {
        set((state) => ({ items: [...state.items, snapshot] }));
      }
      return { success: false, error: (e as Error).message };
    }
  },

  moveItem: async (id, horizon) => {
    return get().updateItem(id, { horizon });
  },

  // ── Selectors ───────────────────────────────────────────

  getDocumentById: (id) => get().documents.find((d) => d.id === id),
  getItemsForRoadmap: (roadmapId) =>
    get().items.filter((i) => i.roadmapId === roadmapId),
  getItemsForHorizon: (roadmapId, horizon) =>
    get()
      .items.filter((i) => i.roadmapId === roadmapId && i.horizon === horizon)
      .sort((a, b) => a.position - b.position),
}));
